import {
    createEventsHeaderText,
    navigateCalendarHeaderText,
    calendarActionsHeaderText,
    goToHeaderText_0,
    openAgendaInMonthViewCommand,
    newEventCommand,
    saveAppointmentCommand,
    sendMeetingCommand,
    discardDraftCommand_0,
    insertHyperlinkCommand_0,
    undoActionCommand,
    retryActionCommand,
    goToTodayCommand,
    goToPreviousTimePeriodCommand,
    goToNextTimePeriodCommand,
    openDayViewCommand,
    openWorkWeekViewCommand,
    openSchoolWeekViewCommand,
    openWeekViewCommand,
    openMonthViewCommand,
    focusSkipLinksCommand,
    openEventCommand,
    closeEventCommand,
    deleteEventCommand,
    printCalendarCommand,
    searchCalendarsCommand,
    goToMailCommand_0,
    goToCalendarCommand_0,
    goToPeopleCommand_0,
    goToTasksCommand_0,
    showKeyboardShortcutsCommand_0,
} from './calendarModuleHotKeys.locstring.json';
import loc from 'owa-localize';

import { CommandCategory } from 'owa-hotkeys-map';
import { isEdu } from 'owa-nonboot-userconfiguration-manager';

const createEventsCategoryKey = 'createEvents';
const navigateCalendarCategoryKey = 'navigateCalendar';
const calendarActionsCategoryKey = 'calendarActions';
const goToCategoryKey = 'goTo';

export function getCommandCategories(): CommandCategory[] {
    return [
        {
            category: createEventsCategoryKey,
            displayName: loc(createEventsHeaderText),
        },
        {
            category: navigateCalendarCategoryKey,
            displayName: loc(navigateCalendarHeaderText),
        },
        {
            category: calendarActionsCategoryKey,
            displayName: loc(calendarActionsHeaderText),
        },
        {
            category: goToCategoryKey,
            displayName: loc(goToHeaderText_0),
        },
    ];
}

type HotkeyBinding = {
    category: string;
    description: string;
    hotmail: string | string[];
    yahoo: string | string[];
    gmail: string | string[];
    owa: string | string[];
};

export function getCommands(): { [key: string]: HotkeyBinding } {
    return {
        newEvent: {
            category: createEventsCategoryKey,
            description: loc(newEventCommand),
            hotmail: ['n'],
            yahoo: ['n'],
            gmail: ['n'],
            owa: ['n'],
        },
        saveAppointment: {
            category: createEventsCategoryKey,
            description: loc(saveAppointmentCommand),
            hotmail: 'ctrl+s',
            yahoo: 'ctrl+s',
            gmail: 'ctrl+s',
            owa: 'ctrl+s',
        },
        sendMeeting: {
            category: createEventsCategoryKey,
            description: loc(sendMeetingCommand),
            hotmail: ['ctrl+enter', 'alt+s'],
            yahoo: ['ctrl+enter', 'alt+s'],
            gmail: ['ctrl+enter', 'alt+s'],
            owa: ['ctrl+enter', 'alt+s'],
        },
        // this hotkey is display-only
        // the actual keyboard shortcut-handling logic is implemented in various compose forms, and does not reference this definition
        discardDraft: {
            category: createEventsCategoryKey,
            description: loc(discardDraftCommand_0),
            hotmail: 'esc',
            yahoo: 'esc',
            gmail: 'esc',
            owa: 'esc',
        },
        // this hotkey is display-only
        // the actual keyboard shortcut-handling logic is in RibbonPlugin, and does not reference this definition
        insertHyperlink: {
            category: createEventsCategoryKey,
            description: loc(insertHyperlinkCommand_0),
            hotmail: 'ctrl+k',
            yahoo: 'ctrl+k',
            gmail: 'ctrl+k',
            owa: 'ctrl+k',
        },
        undoAction: {
            category: createEventsCategoryKey,
            description: loc(undoActionCommand),
            hotmail: 'ctrl+z',
            yahoo: 'ctrl+z',
            gmail: 'ctrl+z',
            owa: 'ctrl+z',
        },
        retryAction: {
            category: createEventsCategoryKey,
            description: loc(retryActionCommand),
            hotmail: 'ctrl+shift+y',
            yahoo: 'ctrl+shift+y',
            gmail: 'ctrl+shift+y',
            owa: 'ctrl+shift+y',
        },
        goToToday: {
            category: navigateCalendarCategoryKey,
            description: loc(goToTodayCommand),
            hotmail: ['shift+alt+y', 'home'],
            yahoo: ['shift+alt+y', 'home'],
            gmail: ['shift+alt+y', 'home'],
            owa: ['shift+alt+y', 'home'],
        },
        prevDateRange: {
            category: navigateCalendarCategoryKey,
            description: loc(goToPreviousTimePeriodCommand),
            hotmail: 'shift+left',
            yahoo: 'shift+left',
            gmail: 'shift+left',
            owa: 'shift+left',
        },
        nextDateRange: {
            category: navigateCalendarCategoryKey,
            description: loc(goToNextTimePeriodCommand),
            hotmail: 'shift+right',
            yahoo: 'shift+right',
            gmail: 'shift+right',
            owa: 'shift+right',
        },
        dayView: {
            category: navigateCalendarCategoryKey,
            description: loc(openDayViewCommand),
            hotmail: 'shift+alt+1',
            yahoo: 'shift+alt+1',
            gmail: 'shift+alt+1',
            owa: 'shift+alt+1',
        },
        workWeekView: {
            category: navigateCalendarCategoryKey,
            description: isEdu() ? loc(openSchoolWeekViewCommand) : loc(openWorkWeekViewCommand),
            hotmail: 'shift+alt+2',
            yahoo: 'shift+alt+2',
            gmail: 'shift+alt+2',
            owa: 'shift+alt+2',
        },
        weekView: {
            category: navigateCalendarCategoryKey,
            description: loc(openWeekViewCommand),
            hotmail: 'shift+alt+3',
            yahoo: 'shift+alt+3',
            gmail: 'shift+alt+3',
            owa: 'shift+alt+3',
        },
        monthView: {
            category: navigateCalendarCategoryKey,
            description: loc(openMonthViewCommand),
            hotmail: 'shift+alt+4',
            yahoo: 'shift+alt+4',
            gmail: 'shift+alt+4',
            owa: 'shift+alt+4',
        },
        agendaMonthView: {
            category: navigateCalendarCategoryKey,
            description: loc(openAgendaInMonthViewCommand),
            hotmail: 'shift+alt+5',
            yahoo: 'shift+alt+5',
            gmail: 'shift+alt+5',
            owa: 'shift+alt+5',
        },
        // this hotkey is for initial focus on skip links
        // further keyboard shortcut-handling logic (i.e. navigating through skip links) is implemented in SkipLinkControl, and does not reference this definition
        focusSkipLinks: {
            category: navigateCalendarCategoryKey,
            description: loc(focusSkipLinksCommand),
            hotmail: 'ctrl+f6',
            yahoo: 'ctrl+f6',
            gmail: 'ctrl+f6',
            owa: 'ctrl+f6',
        },
        // this hotkey is display-only
        // the actual keyboard shortcut-handling logic is in OwaCalendarSurfaceEventHandlers, and does not reference this definition
        openEvent: {
            category: calendarActionsCategoryKey,
            description: loc(openEventCommand),
            hotmail: 'enter',
            yahoo: 'enter',
            gmail: 'enter',
            owa: 'enter',
        },
        // this hotkey is display-only
        // the actual keyboard shortcut-handling logic is implemented in QuickComposeRenderer, and does not reference this definition
        closeEvent: {
            category: calendarActionsCategoryKey,
            description: loc(closeEventCommand),
            hotmail: 'esc',
            yahoo: 'esc',
            gmail: 'esc',
            owa: 'esc',
        },
        deleteEvent: {
            category: calendarActionsCategoryKey,
            description: loc(deleteEventCommand),
            hotmail: 'del',
            yahoo: 'del',
            gmail: 'del',
            owa: 'del',
        },
        printCalendar: {
            category: calendarActionsCategoryKey,
            description: loc(printCalendarCommand),
            hotmail: 'ctrl+p',
            yahoo: 'ctrl+p',
            gmail: 'ctrl+p',
            owa: 'ctrl+p',
        },
        gotoSearch: {
            category: calendarActionsCategoryKey,
            description: loc(searchCalendarsCommand),
            hotmail: '/',
            yahoo: 's',
            gmail: '/',
            owa: 'alt+q',
        },
        gotoMail: {
            category: goToCategoryKey,
            description: loc(goToMailCommand_0),
            hotmail: 'ctrl+shift+1',
            yahoo: 'ctrl+shift+1',
            gmail: 'ctrl+shift+1',
            owa: 'ctrl+shift+1',
        },
        gotoCalendar: {
            category: goToCategoryKey,
            description: loc(goToCalendarCommand_0),
            hotmail: 'ctrl+shift+2',
            yahoo: 'ctrl+shift+2',
            gmail: 'ctrl+shift+2',
            owa: 'ctrl+shift+2',
        },
        gotoPeople: {
            category: goToCategoryKey,
            description: loc(goToPeopleCommand_0),
            hotmail: 'ctrl+shift+3',
            yahoo: 'ctrl+shift+3',
            gmail: 'ctrl+shift+3',
            owa: 'ctrl+shift+3',
        },
        gotoTasks: {
            category: goToCategoryKey,
            description: loc(goToTasksCommand_0),
            hotmail: 'ctrl+shift+4',
            yahoo: 'ctrl+shift+4',
            gmail: 'ctrl+shift+4',
            owa: 'ctrl+shift+4',
        },
        keyboardShortcuts: {
            category: goToCategoryKey,
            description: loc(showKeyboardShortcutsCommand_0),
            hotmail: '?',
            yahoo: '?',
            gmail: '?',
            owa: '?',
        },
    };
}
